
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Clock, User, MapPin, Edit, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const StaffTreatments = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const treatments = [
    {
      id: '1',
      name: 'Botox',
      category: 'Injectable',
      duration: 30,
      price: 450,
      credits: 450,
      description: 'Botulinum toxin treatment for wrinkles',
      status: 'active',
      provider: 'Dr. Smith'
    },
    {
      id: '2',
      name: 'HydraFacial',
      category: 'Facial',
      duration: 60,
      price: 200,
      credits: 200,
      description: 'Deep cleansing and hydrating facial treatment',
      status: 'active',
      provider: 'Emily R.'
    },
    {
      id: '3',
      name: 'Lip Filler',
      category: 'Injectable',
      duration: 45,
      price: 650,
      credits: 650,
      description: 'Hyaluronic acid lip enhancement',
      status: 'active',
      provider: 'Dr. Smith'
    },
    {
      id: '4',
      name: 'Chemical Peel',
      category: 'Facial',
      duration: 30,
      price: 150,
      credits: 150,
      description: 'Exfoliating treatment for skin renewal',
      status: 'inactive',
      provider: 'Sarah M.'
    }
  ];

  const filteredTreatments = treatments.filter(treatment =>
    treatment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    treatment.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Injectable': return 'bg-blue-100 text-blue-800';
      case 'Facial': return 'bg-purple-100 text-purple-800';
      case 'Laser': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-juv-cream to-juv-sage/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold font-juv-heading text-juv-deep-chestnut">
                Treatment Management
              </h1>
              <p className="mt-1 text-juv-deep-chestnut/70">
                Manage available treatments and services
              </p>
            </div>
            <Button asChild className="bg-juv-terracotta text-white">
              <Link to="/staff/treatments/new">
                <Plus className="h-4 w-4 mr-2" />
                Add New Treatment
              </Link>
            </Button>
          </div>

          {/* Search */}
          <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
            <CardContent className="p-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search treatments by name or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Treatment List */}
          <div className="grid gap-4">
            {filteredTreatments.map((treatment) => (
              <Card key={treatment.id} className="bg-white shadow-luxury border border-juv-rose-clay/30 hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-3">
                        <h3 className="text-lg font-semibold text-juv-deep-chestnut">
                          {treatment.name}
                        </h3>
                        <Badge className={getCategoryColor(treatment.category)}>
                          {treatment.category}
                        </Badge>
                        <Badge className={getStatusColor(treatment.status)}>
                          {treatment.status}
                        </Badge>
                      </div>
                      
                      <p className="text-gray-600 mb-3">{treatment.description}</p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-juv-terracotta" />
                          <span>{treatment.duration} mins</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">Price: ${treatment.price}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">Credits: {treatment.credits}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span>{treatment.provider}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Actions */}
          <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
            <CardHeader>
              <CardTitle className="font-juv-heading text-juv-deep-chestnut">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button asChild variant="outline" className="h-20 flex-col space-y-2">
                  <Link to="/staff/treatments/categories">
                    <Plus className="h-5 w-5" />
                    <span>Manage Categories</span>
                  </Link>
                </Button>
                <Button asChild variant="outline" className="h-20 flex-col space-y-2">
                  <Link to="/staff/treatments/pricing">
                    <span className="text-lg">$</span>
                    <span>Update Pricing</span>
                  </Link>
                </Button>
                <Button asChild variant="outline" className="h-20 flex-col space-y-2">
                  <Link to="/staff/treatments/providers">
                    <User className="h-5 w-5" />
                    <span>Manage Providers</span>
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StaffTreatments;
